import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import type { DepotBrandingDto } from '../types';

interface Props {
  branding: DepotBrandingDto;
}

/**
 * En-tête de confiance affiché en haut de chaque écran du dépôt sécurisé.
 *
 * Affiche le logo du cabinet courtier (URL signée Azure Blob, qui peut
 * expirer — on bascule en fallback texte si le chargement échoue), suivi
 * du nom du cabinet et du produit assurance pour que le souscripteur
 * reconnaisse instantanément le contexte et n'ait pas l'impression
 * d'arriver sur un phishing.
 */
export const DepotBrandingHeader: React.FC<Props> = ({ branding }) => {
  const [logoFailed, setLogoFailed] = useState(false);

  const {
    plateformeName,
    productName,
    productOwnerName,
    logoUrl
  } = branding;

  // Si tout est vide, ne rien afficher — évite un header vide qui aurait
  // l'effet inverse de ce qu'on cherche (visuel "rien à dire").
  if (
    !plateformeName &&
    !productName &&
    !productOwnerName &&
    !logoUrl
  ) {
    return null;
  }

  const showLogo = !!logoUrl && !logoFailed;

  return (
    <div className="flex flex-col items-center gap-3 pb-6 border-b border-gray-200 mb-6">
      {showLogo ? (
        <img
          src={logoUrl}
          alt={plateformeName ?? productOwnerName ?? 'Logo'}
          className="h-12 max-w-[200px] object-contain"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        // Fallback générique si l'URL Azure a expiré ou si pas de logo —
        // au moins on garde un repère visuel.
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-gray-400" />
        </div>
      )}
      <div className="text-center">
        {plateformeName && (
          <p className="font-semibold text-gray-800">{plateformeName}</p>
        )}
        {productName && (
          <p className="text-sm text-gray-500">
            {productName}
            {productOwnerName && plateformeName !== productOwnerName ? (
              <>
                {' '}
                · <span className="text-gray-400">{productOwnerName}</span>
              </>
            ) : null}
          </p>
        )}
      </div>
    </div>
  );
};
